import { authQueryKeys, orgQueryKeys } from '@/config/queryKeys';
import { ROUTE_PATHS, adminRoles, publicRoutes } from '@/constants';
import { useAuthStatus } from '@/hooks';
// Handle post-login redirect
import { usePostLoginRedirect } from '@/hooks/usePostLoginRedirect';
import { fetchOrganizations } from '@/pages/Organizations/orgUtils';
import { useAppStore } from '@/stores/app';
import { SUPER_ADMIN_ROLE } from '@/types';
import type { AuthData } from '@/types/auth';
import { Layout, Sidebar } from '@components/index';
import { mainContent } from '@components/Layout/styles.module.css';
import type { SidebarLinkProps } from '@components/Sidebar/types';
import { VoyagerIcon } from '@nautilusbio/nb-design-system';
import { Toaster } from '@nautilusbio/nb-design-system/Toaster';
import { TanStackDevtools } from '@tanstack/react-devtools';
import '@/styles/components/ag-grid-overrides.css';
import { FormDevtoolsPanel } from '@tanstack/react-form-devtools';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  redirect,
  useLocation,
} from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel, Separator, usePanelRef } from 'react-resizable-panels';
import { useShallow } from 'zustand/react/shallow';

import globalStyles from '../styles/index.module.css';
import pageStyles from '../styles/pages/index.module.css';

type RouterContext = {
  authentication: AuthData;
  queryClient: QueryClient;
};

const tanstackPlugins = [
  {
    name: 'TanStack Query',
    render: <ReactQueryDevtoolsPanel />,
    defaultOpen: false,
  },
  {
    name: 'TanStack Router',
    render: <TanStackRouterDevtoolsPanel />,
    defaultOpen: false,
  },
  {
    name: 'TanStack Form',
    render: <FormDevtoolsPanel theme="dark" />,
    defaultOpen: false,
  },
];
const tanstackConfig = { hideUntilHover: true };
const toastOptions = {
  duration: 2000,
};
// TODO: Error/404 Handling

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: ({ context }) => {
    // Skip auth check for login page
    if (window.location.pathname === ROUTE_PATHS.LOGIN) {
      return;
    }

    // Don't redirect during a server-side post-login callback where the
    // session cookie may not yet be visible to the first /auth/me probe.
    // This prevents bouncing back to /login immediately after a successful
    // external login redirect.
    if (window.location.search.includes('login=success')) {
      // Skip redirect during post-login callback; logging removed.
      return;
    }

    // Don't redirect if we're still loading the auth state
    // This is critical - we must wait for the auth query to complete
    if (context.authentication.status === 'PENDING') {
      return;
    }

    // Only redirect if explicitly unauthenticated (not pending).
    // NOTE: Do NOT call clearClientSession() here — beforeLoad runs on every
    // route transition and the auth status can be transiently UNAUTHENTICATED
    // during query refetches or org switches. Session cleanup is handled by
    // the 401 handler in router.tsx (triggered by actual API 401 responses).
    if (context.authentication.status === 'UNAUTHENTICATED') {
      throw redirect({ to: '/login' });
    }
  },
  component: () => {
    const location = useLocation();

    const showSidebar = useMemo(
      () => !publicRoutes.some(route => location.pathname.includes(route)),
      [location?.pathname],
    );

    const { user } = useAuthStatus();
    const { setUser, selectedOrganization, selectedOrgStub, setSelectedOrganization } = useAppStore(
      useShallow(s => ({
        setUser: s.setUser,
        selectedOrganization: s.selectedOrganization,
        selectedOrgStub: s.selectedOrgStub,
        setSelectedOrganization: s.setSelectedOrganization,
      })),
    );

    // Update app store with current user data
    useEffect(() => {
      setUser(user ?? undefined);
    }, [user, setUser]);

    // Fetch organizations for the logged-in user
    const { data: orgs } = useQuery({
      enabled: !!user,
      queryKey: orgQueryKeys.list(),
      queryFn: fetchOrganizations,
    });

    // Re-hydrate selectedOrganization from persisted stub after a page reload or
    // after clearClientSession clears the runtime org. Without this, the OrgSelector
    // disappears and InstrumentFormDialog defaults to the wrong org on any route
    // other than "/" (which is the only place org selection normally runs).
    useEffect(() => {
      if (!selectedOrganization && orgs && orgs.length > 0) {
        const targetId = selectedOrgStub?.id;
        const org = targetId ? (orgs.find(o => o.id === targetId) ?? orgs[0]) : orgs[0];
        setSelectedOrganization(org);
      }
    }, [orgs, selectedOrganization, selectedOrgStub, setSelectedOrganization]);

    // Sidebar should be disabled if user is logged in and has no orgs
    const sidebarDisabled = !!user && orgs && orgs.length === 0;

    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isHovering, setIsHovering] = useState<boolean>(false);
    const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const didMountRef = useRef(false);
    const { t } = useTranslation('sidebar');

    usePostLoginRedirect();

    // Proactive auth revalidation on tab focus.
    // Uses both 'visibilitychange' and 'focus' because some browsers (e.g. mobile Safari)
    // don't reliably fire 'focus' when returning from a background tab.
    const queryClient = useQueryClient();
    useEffect(() => {
      const handleFocus = () => {
        // Skip during post-login callback to avoid racing with cookie propagation
        if (window.location.search.includes('login=success')) return;

        // Skip if recently logged out (15s suppression window)
        const lastLogout = sessionStorage.getItem('__last_logout_ts');
        if (lastLogout && Date.now() - Number(lastLogout) < 15_000) return;

        // Skip on public routes — no auth check needed
        if (publicRoutes.some(route => window.location.pathname.includes(route))) return;

        // Invalidate auth query to force an immediate refetch of /auth/me
        void queryClient.invalidateQueries({ queryKey: authQueryKeys.me() });
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') handleFocus();
      };

      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }, [queryClient]);

    const topLevelLinks: SidebarLinkProps[] = useMemo(
      () => [
        {
          children: t('stu'),
          icon: 'notebook-pen',
          preload: 'intent',
          to: '/studies',
        },
        {
          children: t('runs'),
          icon: 'flask-conical',
          preload: 'intent',
          to: '/instrument_runs',
        },
        {
          children: t('sam'),
          icon: 'microscope',
          roles: [SUPER_ADMIN_ROLE],
          preload: 'intent',
          to: '/samples',
        },
        {
          children: t('use'),
          icon: 'user-round-cog',
          roles: adminRoles,
          to: '/users',
        },
        {
          children: t('instr'),
          icon: VoyagerIcon,
          roles: adminRoles,
          preload: 'intent',
          to: '/instruments',
        },
        {
          children: t('org'),
          icon: 'briefcase-business',
          roles: adminRoles,
          to: '/organizations',
        },
        // { children: t('fle'), icon: 'computer', to: '/fleet' },
        { children: t('aut'), icon: 'key', roles: adminRoles, to: '/auth' },
      ],
      [t],
    );
    const sidebarRef = usePanelRef();
    // elementRef gives us the outer wrapper div (overflow: visible); the actual
    // scrollable element is its first child (overflow: auto, where scroll events fire).
    // We stamp data-scroll-restoration-id on the inner div so TanStack Router's
    // scroll restoration tracks it with a stable attribute selector rather than a
    // fragile nth-child CSS path.
    const mainPanelInnerRef = useCallback((el: HTMLDivElement | null) => {
      const scrollable = el?.firstElementChild as HTMLDivElement | null;
      scrollable?.setAttribute('data-scroll-restoration-id', 'main-scrollable-area');
    }, []);

    const resizerDblClickHandler = useCallback(() => {
      return sidebarRef?.current?.isCollapsed() ? sidebarRef?.current?.expand() : sidebarRef?.current?.collapse();
    }, [sidebarRef]);

    const handleLayoutChange = useCallback(() => {
      if (!didMountRef.current) {
        didMountRef.current = true;
        return;
      }

      setIsDragging(true);

      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }

      dragTimeoutRef.current = setTimeout(() => {
        setIsDragging(false);
      }, 100);
    }, []);

    useEffect(() => {
      return () => {
        if (dragTimeoutRef.current) {
          clearTimeout(dragTimeoutRef.current);
        }
      };
    }, []);

    const handleSeparatorMouseEnter = useCallback(() => {
      setIsHovering(true);
    }, []);
    const handleSeparatorMouseLeave = useCallback(() => {
      setIsHovering(false);
    }, []);

    return (
      <>
        <HeadContent />

        <Scripts />

        <Layout disabled={!user} onLayoutChange={handleLayoutChange}>
          {showSidebar && (
            <>
              <Sidebar
                disabled={sidebarDisabled}
                isInteracting={isDragging || isHovering}
                links={topLevelLinks}
                sidebarRef={sidebarRef}
              />
              <Separator
                className={clsx(
                  pageStyles.resizeHandle,
                  isDragging && pageStyles.resizing,
                  isHovering && pageStyles.resizeHover,
                )}
                onDoubleClick={resizerDblClickHandler}
                onMouseEnter={handleSeparatorMouseEnter}
                onMouseLeave={handleSeparatorMouseLeave}
              />
            </>
          )}

          <Panel
            elementRef={mainPanelInnerRef}
            className={clsx(mainContent, globalStyles.scrollbars, 'maincontent-panel')}
          >
            <Outlet />
          </Panel>
        </Layout>

        <Toaster toastOptions={toastOptions} />

        {import.meta.env.VITE_ENABLE_DEVTOOLS === 'true' && (
          <TanStackDevtools config={tanstackConfig} plugins={tanstackPlugins} />
        )}
      </>
    );
  },
  // No longer passing selectedOrganization into context; access it directly from the store where needed
  head: () => ({
    meta: [
      {
        name: 'Nautilus Biotechnology - Customer Portal',
        content: 'App for interacting with instruments.',
      },
      {
        title: 'Nautilus Biotechnology - Customer Portal',
      },
    ],
  }),
});
